import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, XCircle, Lightbulb } from 'lucide-react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';

/**
 * JSIntroduction — the "Introduction to JavaScript" article, authored to the
 * Platform-Page-Layout architecture (Real Problem → Definition → ... →
 * Most Important Takeaway). Calm, editorial design: neutral surfaces with a
 * single amber accent (JavaScript's identity color), used sparingly.
 */

type Author = { name: string; fullName: string; avatar?: string };

// ---- small presentational helpers -------------------------------------

const SectionHeading: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
 <div className={`flex items-center gap-3 mb-5 ${n === 1 ? '' : 'mt-10 pt-6 border-t border-gray-200 dark:border-gray-700'}`}>
  <span className="article-section-number article-number-badge flex items-center justify-center w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-bold">{n}</span>
  <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{children}</h2>
 </div>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <p className="text-[15px] leading-7 text-gray-700 dark:text-gray-300 mb-4">{children}</p>
);

const Mark: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <span className="font-semibold text-gray-900 dark:text-white">{children}</span>
);

const Callout: React.FC<{ tone: 'tip' | 'warning' | 'info'; title?: string; children: React.ReactNode }> = ({ tone, title, children }) => {
 const map = {
  tip: { border: 'border-green-400', bg: 'bg-green-50/60 dark:bg-green-900/10', icon: <CheckCircle2 className="w-5 h-5 text-green-500" /> },
  warning: { border: 'border-red-400', bg: 'bg-red-50/60 dark:bg-red-900/10', icon: <XCircle className="w-5 h-5 text-red-500" /> },
  info: { border: 'border-amber-400', bg: 'bg-amber-50/60 dark:bg-amber-900/10', icon: <Lightbulb className="w-5 h-5 text-amber-500" /> },
 }[tone];
 return (
  <div className={`flex items-start gap-3 pl-4 pr-4 py-3 border-l-2 rounded-r-lg ${map.border} ${map.bg} mb-4`}>
   <span className="mt-0.5 flex-shrink-0">{map.icon}</span>
   <div className="text-sm leading-6 text-gray-700 dark:text-gray-300">
    {title && <span className="font-semibold text-gray-900 dark:text-white">{title}: </span>}
    {children}
   </div>
  </div>
 );
};

// A small terminal-style output box for "Output:" snippets.
const Output: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
  <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">Output</div>
  <pre className="px-4 py-3 bg-gray-900 text-green-300 text-sm font-mono">{children}</pre>
 </div>
);

const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <span className="px-2.5 py-1 rounded-md text-[12px] font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">{children}</span>
);

// ---- inline diagrams ---------------------------------------------------

// 1. "Static page" problem: click -> nothing
const StaticPageFlow: React.FC = () => (
 <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-5">
  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-500 mb-4">A static page, before JavaScript</div>
  <div className="flex items-center justify-center gap-3 sm:gap-4 flex-wrap">
   <div className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-200">User clicks button</div>
   <span className="text-gray-400 text-xl">→</span>
   <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-sm font-medium text-red-700 dark:text-red-300">
    <XCircle className="w-4 h-4" /> Nothing happens
   </div>
  </div>
 </div>
);

// 4. Event-driven model: events feed the engine, which runs handlers
const EventDrivenFlow: React.FC = () => {
 const events = ['Click', 'Input', 'Network response', 'Timer'];
 return (
  <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-5">
   <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-500 mb-4">Event-driven execution</div>
   <div className="flex items-center justify-between gap-4 flex-col sm:flex-row">
    <div className="flex flex-col gap-2">
     {events.map((e) => (
      <div key={e} className="px-3 py-1.5 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-[12px] font-medium text-amber-700 dark:text-amber-300 text-center">{e}</div>
     ))}
    </div>
    <span className="text-gray-400 text-xl rotate-90 sm:rotate-0">→</span>
    <div className="px-5 py-4 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-center">
     <div className="text-sm font-bold text-gray-900 dark:text-white">JavaScript Engine</div>
     <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5">event loop waits & dispatches</div>
    </div>
    <span className="text-gray-400 text-xl rotate-90 sm:rotate-0">→</span>
    <div className="px-4 py-2.5 rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-sm font-medium text-green-700 dark:text-green-300 text-center">Run the<br />matching logic</div>
   </div>
  </div>
 );
};

// 11. Mental model: HTML/CSS/JS = Skeleton/Skin/Brain
const MentalModelCards: React.FC = () => {
 const cards = [
  { tech: 'HTML', role: 'Skeleton', desc: 'Structure & content', emoji: '🦴' },
  { tech: 'CSS', role: 'Skin', desc: 'Styling & appearance', emoji: '🎨' },
  { tech: 'JavaScript', role: 'Brain', desc: 'Behavior & logic', emoji: '🧠', accent: true },
 ];
 return (
  <div className="my-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
   {cards.map((c) => (
    <div key={c.tech} className={`rounded-xl border p-4 text-center ${c.accent ? 'border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/15' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'}`}>
     <div className="text-2xl mb-1">{c.emoji}</div>
     <div className="text-sm font-bold text-gray-900 dark:text-white">{c.tech}</div>
     <div className={`text-[13px] font-semibold ${c.accent ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>{c.role}</div>
     <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">{c.desc}</div>
    </div>
   ))}
  </div>
 );
};

const UsageGrid: React.FC = () => {
 const groups = [
  { label: 'Frontend', items: ['React', 'Angular', 'Vue'] },
  { label: 'Backend', items: ['Node.js', 'NestJS', 'Express'] },
  { label: 'Mobile', items: ['React Native'] },
  { label: 'Desktop', items: ['Electron'] },
  { label: 'Cloud', items: ['AWS Lambda', 'Serverless'] },
 ];
 return (
  <div className="my-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
   {groups.map((g) => (
    <div key={g.label} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">{g.label}</div>
     <div className="flex flex-wrap gap-1.5">{g.items.map((i) => <Pill key={i}>{i}</Pill>)}</div>
    </div>
   ))}
  </div>
 );
};

// ---- the article -------------------------------------------------------

export const JSIntroduction: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Introduction to JavaScript</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    The language that turned the web from static pages into living, interactive applications — and then grew to run almost everywhere.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="9 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>In the early days of the web, websites were <Mark>static</Mark>. A page could display information, but it couldn&apos;t react to anything the user did. Clicking a button did exactly nothing.</Para>
   <StaticPageFlow />
   <Para>Websites needed a way to respond to interactions, validate forms, update content on the fly, and talk to servers — all <Mark>inside the browser</Mark>. A programming language was needed for exactly that. That language became JavaScript.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">
     <Mark>JavaScript</Mark> is a high-level, interpreted programming language primarily used to create interactive and dynamic web applications.
    </p>
   </div>
   <Para>Today, JavaScript runs far beyond the browser:</Para>
   <div className="flex flex-wrap gap-2">
    {['Browsers', 'Servers (Node.js)', 'Mobile apps', 'Desktop apps', 'Cloud functions'].map((x) => <Pill key={x}>{x}</Pill>)}
   </div>
  </section>

  {/* 3. Why It Exists */}
  <section className="mb-12">
   <SectionHeading n={3}>Why It Exists</SectionHeading>
   <Para>JavaScript was created to solve one simple problem: <Mark>make web pages interactive</Mark>. Without it, a button is just markup:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `<button>Submit</button>\n// Clicking this does nothing on its own.` }]} defaultLanguage="javascript" id="js-why-exists" />
   <Para>With JavaScript, that same click can execute logic and update the UI in response.</Para>
  </section>

  {/* 4. Core Idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea</SectionHeading>
   <Para>JavaScript is an <Mark>event-driven</Mark> language. Instead of only running top-to-bottom and exiting, it waits for events — and then runs the logic attached to each one.</Para>
   <EventDrivenFlow />
   <Para>This event-driven nature is a big part of why JavaScript became the ideal language for the web.</Para>
  </section>

  {/* 5. Key Characteristics */}
  <section className="mb-12">
   <SectionHeading n={5}>Key Characteristics</SectionHeading>

   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Dynamic Typing</h3>
   <Para>Variables don&apos;t require explicit types — the same variable can hold different types over time.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let value = 10;     // number\nvalue = "Hello";    // now a string — perfectly legal` }]} defaultLanguage="javascript" id="js-dynamic-typing" />

   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Interpreted Language</h3>
   <Para>Code is executed by a JavaScript engine, with no separate compile step:</Para>
   <div className="flex flex-wrap gap-2 mb-5">
    {['V8 (Chrome, Node.js)', 'SpiderMonkey (Firefox)', 'JavaScriptCore (Safari)'].map((x) => <Pill key={x}>{x}</Pill>)}
   </div>

   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Multi-Paradigm</h3>
   <Para>It supports procedural, object-oriented, and functional programming styles.</Para>

   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Single-Threaded</h3>
   <Para>JavaScript runs on a single main thread. Concurrency is achieved through the event loop, callbacks, promises, and async/await — not extra threads.</Para>
  </section>

  {/* 6. Where JavaScript Is Used */}
  <section className="mb-12">
   <SectionHeading n={6}>Where JavaScript Is Used</SectionHeading>
   <UsageGrid />
  </section>

  {/* 7 & 8. Examples */}
  <section className="mb-12">
   <SectionHeading n={7}>Examples</SectionHeading>
   <Para>The classic first program:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("Hello World");` }]} defaultLanguage="javascript" id="js-hello" />
   <Output>Hello World</Output>

   <Para>A slightly more practical one — a function that builds a greeting:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function greet(name) {\n  return \`Hello \${name}\`;\n}\n\nconsole.log(greet("Sachin"));` }]} defaultLanguage="javascript" id="js-greet" />
   <Output>Hello Sachin</Output>
  </section>

  {/* 9. Interview importance */}
  <section className="mb-12">
   <SectionHeading n={8}>Why It Matters for Interviews</SectionHeading>
   <Para>Most candidates know the syntax. Interviewers test <Mark>understanding</Mark> — and the questions cluster around a handful of topics:</Para>
   <div className="flex flex-wrap gap-2">
    {['Closures', 'Promises', 'Event Loop', 'this keyword', 'Async/Await', 'Prototypes', 'Hoisting'].map((x) => <Pill key={x}>{x}</Pill>)}
   </div>
  </section>

  {/* 10. Common Misconceptions */}
  <section className="mb-12">
   <SectionHeading n={9}>Common Misconceptions</SectionHeading>
   <Callout tone="warning" title="JavaScript = Java">Completely different languages. The shared name was a 1990s marketing decision, nothing more.</Callout>
   <Callout tone="warning" title="JavaScript only runs in browsers">Modern JavaScript runs on servers, mobiles, desktops, and the cloud.</Callout>
   <Callout tone="warning" title="JavaScript can't handle large systems">Large-scale systems are built every day with Node.js, TypeScript, and NestJS.</Callout>
  </section>

  {/* 11. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={10}>Mental Model</SectionHeading>
   <Para>Think of a web page as a body, built from three technologies that each play a distinct role:</Para>
   <MentalModelCards />
  </section>

  {/* 12 & 13. One-liner + takeaway */}
  <section className="mb-4">
   <SectionHeading n={11}>Interview One-Liner & Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     JavaScript is a high-level, event-driven programming language used to build interactive applications across browsers, servers, mobile devices, and the cloud.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    JavaScript is no longer just a browser language. It has evolved into a full-stack ecosystem powering frontend, backend, mobile, desktop, and cloud applications.
   </Callout>
  </section>

  {/* subtle entrance for the whole article on the accent line */}
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} />
 </div>
);

export default JSIntroduction;
